import type { AnalyticsReportingMode, Genie2ReportingMode } from '~/utils/analyticsReportingMode';
import { ANALYTICS_REPORTING_MODE_LABELS } from '~/utils/analyticsReportingMode';

export type ColumnAccessSelectionDiff = {
  addedLabels: string[];
  removedLabels: string[];
};

export function diffColumnAccessSelection(
  selectedCatalogIds: ReadonlySet<string>,
  serverAllowSet: ReadonlySet<string>,
  catalogLabelById: ReadonlyMap<string, string>,
): ColumnAccessSelectionDiff {
  const addedLabels: string[] = [];
  const removedLabels: string[] = [];

  for (const id of selectedCatalogIds) {
    if (!serverAllowSet.has(id)) {
      addedLabels.push(catalogLabelById.get(id) ?? id);
    }
  }
  for (const id of serverAllowSet) {
    if (!selectedCatalogIds.has(id)) {
      removedLabels.push(catalogLabelById.get(id) ?? id);
    }
  }

  addedLabels.sort((a, b) => a.localeCompare(b));
  removedLabels.sort((a, b) => a.localeCompare(b));

  return { addedLabels, removedLabels };
}

export type ColumnAccessGoLiveWarningOptions = {
  /** Parent hub applies always persist the parent's Genie 2.0 mode onto the child. */
  audience?: 'staff' | 'parent';
  /** Child/account persisted mode before this write (null while unknown). */
  currentMode?: AnalyticsReportingMode | null;
};

export function getColumnAccessGoLiveWarning(
  mode: Genie2ReportingMode,
  options: ColumnAccessGoLiveWarningOptions = {},
): {
  lead: string;
  detail: string;
} {
  const modeLabel = ANALYTICS_REPORTING_MODE_LABELS[mode];
  const { audience = 'staff', currentMode } = options;
  const leavingClassicGenie = currentMode == null || currentMode === 'legacy';

  if (audience === 'parent') {
    if (leavingClassicGenie) {
      return {
        lead: 'Classic analytics will be removed for this child.',
        detail:
          'Submitting replaces classic analytics with the new analytics the parent account uses. This child will use the new analytics from then on.',
      };
    }
    return {
      lead: "This child will switch to the parent's analytics.",
      detail:
        'Submitting column access always matches the child to the parent account. Classic analytics stays unavailable.',
    };
  }

  return {
    lead: 'This account will lose Classic Genie.',
    detail: `Submitting assigns ${modeLabel} reporting. Classic Genie will no longer be available for this account.`,
  };
}

/** Parent Apply always persists parent Genie 2.0 onto the child; confirm when that changes the child. */
export function shouldConfirmParentColumnAccessGoLive(args: {
  parentGenie2Mode: Genie2ReportingMode | null;
  childPersistedMode: AnalyticsReportingMode | null;
}): boolean {
  const { parentGenie2Mode, childPersistedMode } = args;
  if (parentGenie2Mode == null) return false;
  // Unknown persisted mode still go-lives: parent apply assigns the parent set.
  return childPersistedMode !== parentGenie2Mode;
}
